import BackgroundImage from "gatsby-background-image";
import { PropsWithChildren } from "react";
import styled from "styled-components";
import { Box, Heading, Section } from "@vinomofo/components";

import { ContentfulPageProps } from "../../types/vino-data-types";
import { generateStorefrontImage } from "../../utils/generate-storefront-image";
import { GatsbyImageFluidProps } from "../../types/gatsby-image-types";

const HeroBackgroundImage = ({
  image,
  children,
  ...rest
}: PropsWithChildren<{ image: GatsbyImageFluidProps }>) => {
  return (
    <BackgroundImage Tag={Box} fluid={image.fluid} {...rest}>
      {children}
    </BackgroundImage>
  );
};

const HeroWrapper = styled(HeroBackgroundImage)`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const HeroText = styled(Section)`
  text-align: center;
  padding-bottom: 40px;

  h1,
  h2 {
    text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.45);
  }

  h1 {
    margin-bottom: 20px;
  }

  &::before {
    z-index: -1;

    content: "";

    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 50%,
      rgba(0, 0, 0, 0.55) 85%
    );

    background-repeat: no-repeat;
  }
`;

const Title = ({ title }: { title: string }) => (
  <HeroText px={[0, "16.664%"]}>
    <Heading variant="xxl" fontSize={["60px", "85px"]} color="black" as="h1">
      {title}
    </Heading>
  </HeroText>
);

const TitleAndSubtitle = ({
  title,
  subTitle,
}: {
  title: string;
  subTitle: string;
}) => (
  <HeroText px={[0, "16.664%"]}>
    <Heading variant="xs" color="brand" as="h1">
      {title}
    </Heading>
    <Heading variant="xxl" fontSize={["60px", "85px"]} color="almostWhite">
      {subTitle}
    </Heading>
  </HeroText>
);

export const LargeHeaderWithImage = ({
  title,
  subTitle,
  heroImage,
}: Pick<
  ContentfulPageProps,
  "title" | "heroImage" | "subTitle"
>): JSX.Element => {
  const heroImg = {
    fluid: {
      ...generateStorefrontImage(heroImage).images.fallback,
      aspectRatio: heroImage.width / heroImage.height,
    },
  };

  return (
    <Box height={[380, 600]} maxWidth="1700px" mx="auto">
      <HeroWrapper image={heroImg} height="100%">
        {subTitle ? (
          <TitleAndSubtitle title={title} subTitle={subTitle} />
        ) : (
          <Title title={title} />
        )}
      </HeroWrapper>
    </Box>
  );
};
